import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAcademicYearPicker, useCampusPicker } from '@/hooks/usePickers';
import type { AcademicYearSummary, CampusSummary, UUID } from '@/types/domain';

/**
 * The campus and academic year every list on the page is read against.
 *
 * Previously each screen carried its own year and campus selects, which meant
 * the answer to "which year am I looking at?" could differ between two tabs of
 * the same product. Promoting it here makes it one answer, set once in the
 * shell's context bar.
 *
 * Both are nullable and null is meaningful: no campus means every campus, and
 * no year means the school's current one. Neither is the same as "not chosen
 * yet", which is why the defaults are resolved from the picker rather than
 * guessed.
 */
export interface WorkingContextValue {
  /** Null means every campus. */
  campusId: UUID | null;
  /** Null until the pickers resolve, then the school's current year. */
  academicYearId: UUID | null;
  setCampusId: (id: UUID | null) => void;
  setAcademicYearId: (id: UUID | null) => void;
  campuses: CampusSummary[];
  academicYears: AcademicYearSummary[];
  /** True while the reference data behind the selectors is still loading. */
  loading: boolean;
}

export const WorkingContext = createContext<WorkingContextValue | null>(null);

const CAMPUS_KEY = 'sms.context.campus';
const YEAR_KEY = 'sms.context.year';

/** localStorage throws in private windows and when site data is blocked. */
function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, value);
    }
  } catch {
    /* A dropped preference is a nuisance, never a failure. */
  }
}

export function WorkingContextProvider({ children }: { children: ReactNode }) {
  const campusPicker = useCampusPicker();
  const yearPicker = useAcademicYearPicker();

  const [campusId, setCampusIdState] = useState<UUID | null>(() => readStored(CAMPUS_KEY));
  const [academicYearId, setYearIdState] = useState<UUID | null>(() => readStored(YEAR_KEY));

  const campuses = useMemo(() => campusPicker.data ?? [], [campusPicker.data]);
  const academicYears = useMemo(() => yearPicker.data ?? [], [yearPicker.data]);

  /*
   * Resolve the defaults once the pickers land, and drop a stored value that no
   * longer exists — a campus can be deactivated between visits, and silently
   * filtering every list by a dead identifier would look like an empty database.
   */
  useEffect(() => {
    if (campuses.length === 0) {
      return;
    }
    if (campusId !== null && !campuses.some((campus) => campus.id === campusId)) {
      setCampusIdState(null);
      writeStored(CAMPUS_KEY, null);
    }
  }, [campuses, campusId]);

  useEffect(() => {
    if (academicYears.length === 0) {
      return;
    }
    const known = academicYears.some((year) => year.id === academicYearId);
    if (academicYearId !== null && known) {
      return;
    }
    // `academicYears` is non-empty here, but index access is still typed as
    // possibly undefined under noUncheckedIndexedAccess.
    const fallback = academicYears.find((year) => year.current) ?? academicYears.at(0);
    if (fallback === undefined) {
      return;
    }
    setYearIdState(fallback.id);
    writeStored(YEAR_KEY, fallback.id);
  }, [academicYears, academicYearId]);

  const setCampusId = useCallback((id: UUID | null) => {
    setCampusIdState(id);
    writeStored(CAMPUS_KEY, id);
  }, []);

  const setAcademicYearId = useCallback((id: UUID | null) => {
    setYearIdState(id);
    writeStored(YEAR_KEY, id);
  }, []);

  const value = useMemo<WorkingContextValue>(
    () => ({
      campusId,
      academicYearId,
      setCampusId,
      setAcademicYearId,
      campuses,
      academicYears,
      loading: campusPicker.isPending || yearPicker.isPending,
    }),
    [
      campusId,
      academicYearId,
      setCampusId,
      setAcademicYearId,
      campuses,
      academicYears,
      campusPicker.isPending,
      yearPicker.isPending,
    ],
  );

  return <WorkingContext.Provider value={value}>{children}</WorkingContext.Provider>;
}
